<template>
  <div class="toolbox">
    <h3 class="toolbox-title">Araçlar</h3>

    <!-- Tool Selection -->
    <div class="tool-section">
      <div class="tool-buttons">
        <button
          v-for="tool in allToolsWithPermissions"
          :key="tool.id"
          :class="[
            'tool-btn', 
            { 
              active: activeTool === tool.id, 
              'select-tool': tool.id === 'select',
              'undo-tool': tool.id === 'undo',
              'redo-tool': tool.id === 'redo',
              'restricted': tool.restricted,
              'disabled': tool.restricted || (tool.id === 'undo' && !canUndo) || (tool.id === 'redo' && !canRedo)
            }
          ]"
          :disabled="tool.restricted || (tool.id === 'undo' && !canUndo) || (tool.id === 'redo' && !canRedo)"
          @click="selectTool(tool.id)"
        >
          <i :class="tool.icon"></i>
          <span>{{ tool.name }}</span>
          <i v-if="tool.restricted" class="fas fa-lock restriction-icon"></i>
        </button>
      </div>
      
      <!-- Guest permissions info -->
      <div v-if="isGuest && hasRestrictedTools" class="permission-notice">
        <small>
          <i class="fas fa-info-circle"></i>
          <span class="restricted-text">Kısıtlı araçlar Oda sahibi izni gerektirir</span>
        </small>
      </div>
    </div>

    <!-- Quick Tool Options -->
    <div class="quick-options" v-if="activeTool !== 'select' && activeTool !== 'undo' && activeTool !== 'redo' && activeTool !== 'eraser'">
      
      <!-- Pen Options -->
      <div v-if="activeTool === 'pen'" class="options-compact">
        <div class="option-row">
          <input 
            type="color" 
            v-model="penOptions.stroke" 
            @input="updateToolOptions"
            class="color-input"
          />
          <input 
            type="range" 
            min="1" 
            max="40" 
            v-model="penOptions.strokeWidth" 
            @input="updateToolOptions"
            class="size-slider"
          />
          <span class="size-display">{{ penOptions.strokeWidth }}px</span>
        </div>
      </div>

      <!-- Shape Options -->
      <div v-if="activeTool === 'rectangle' || activeTool === 'circle'" class="options-compact">
        <div class="option-row">
          <input 
            type="color" 
            v-model="shapeOptions.stroke" 
            @input="updateToolOptions"
            class="color-input"
          />
          <label class="fill-toggle">
            <input 
              type="checkbox" 
              v-model="shapeOptions.filled" 
              @change="updateToolOptions"
            />
            Doldur
          </label>
          <input 
            type="range" 
            min="1" 
            max="20" 
            v-model="shapeOptions.strokeWidth" 
            @input="updateToolOptions"
            class="size-slider"
          />
          <span class="size-display">{{ shapeOptions.strokeWidth }}px</span>
        </div>
      </div>
    </div>

    <!-- Canvas Actions (Host Only) -->
    <div v-if="!isGuest" class="canvas-actions">
      <button 
        class="action-btn clear-btn" 
        @click="clearCanvas"
      >
        <i class="fas fa-trash"></i>
        Temizle
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Toolbox',
  props: {
    activeTool: {
      type: String,
      default: 'pen'
    },
    isGuest: {
      type: Boolean,
      default: false
    },
    permissions: {
      type: Object,
      default: () => ({
        canDraw: true,
        canErase: true,
        canAddShapes: true,
        canUploadBackground: true,
        canClear: true
      })
    },
    canUndo: {
      type: Boolean,
      default: false
    },
    canRedo: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tools: [
        {
          id: 'select',
          name: 'Seç',
          icon: 'fas fa-mouse-pointer'
        },
        {
          id: 'pen',
          name: 'Kalem',
          icon: 'fas fa-pen'
        },
        {
          id: 'eraser',
          name: 'Silgi',
          icon: 'fas fa-eraser'
        },
        {
          id: 'undo',
          name: 'Geri Al',
          icon: 'fas fa-undo'
        },
        {
          id: 'redo',
          name: 'Yinele',
          icon: 'fas fa-redo'
        },
        {
          id: 'rectangle',
          name: 'Dikdörtgen',
          icon: 'fas fa-square'
        },
        {
          id: 'circle',
          name: 'Daire',
          icon: 'fas fa-circle'
        }
      ],
      penOptions: {
        strokeWidth: 10,
        stroke: '#000000'
      },
      rectangleOptions: {
        strokeWidth: 10,
        stroke: '#000000',
        filled: false
      },
      circleOptions: {
        strokeWidth: 10,
        stroke: '#000000',
        filled: false
      }
    }
  },
  computed: {
    availableTools() {
      if (!this.isGuest) {
        return this.tools // Host has access to all tools
      }
      
      // Filter tools based on guest permissions
      return this.tools.filter(tool => {
        switch (tool.id) {
          case 'select':
            return this.permissions.canSelect // Select tool now requires permission
          case 'pen':
            return this.permissions.canDraw
          case 'eraser':
            return this.permissions.canErase
          case 'undo':
            return this.permissions.canUndo
          case 'redo':
            return this.permissions.canRedo
          case 'rectangle':
          case 'circle':
            return this.permissions.canAddShapes
          default:
            return false
        }
      })
    },
    
    allToolsWithPermissions() {
      if (!this.isGuest) {
        return this.tools.map(tool => ({ ...tool, restricted: false }))
      }
      
      // For guests, completely exclude select, undo, and redo tools (permanently restricted)
      const allowedTools = this.tools.filter(tool => {
        // Permanently exclude these tools for guests
        return !['select', 'undo', 'redo'].includes(tool.id)
      })
      
      // For remaining tools, mark restricted ones
      return allowedTools.map(tool => {
        let hasPermission = false
        switch (tool.id) {
          case 'pen':
            hasPermission = this.permissions.canDraw
            break
          case 'eraser':
            hasPermission = this.permissions.canErase
            break
          case 'rectangle':
          case 'circle':
            hasPermission = this.permissions.canAddShapes
            break
          default:
            hasPermission = false
        }
        
        return {
          ...tool,
          restricted: !hasPermission
        }
      })
    },
    
    hasRestrictedTools() {
      return this.isGuest && this.allToolsWithPermissions.some(tool => tool.restricted)
    },
    
    shapeOptions() {
      if (this.activeTool === 'rectangle') {
        return this.rectangleOptions
      } else if (this.activeTool === 'circle') {
        return this.circleOptions
      }
      return {}
    },
    
    canClearCanvas() {
      // Only hosts can clear canvas (guests permanently cannot)
      return !this.isGuest
    }
  },
  methods: {
    selectTool(toolId) {
      // Handle undo/redo actions directly (these are actions, not tools)
      if (toolId === 'undo') {
        this.$emit('undo-action')
        return
      }
      
      if (toolId === 'redo') {
        this.$emit('redo-action')
        return
      }
      
      // Check if this tool is restricted for guests
      if (this.isGuest) {
        const tool = this.allToolsWithPermissions.find(t => t.id === toolId)
        if (tool && tool.restricted) {
          console.log('Tool is restricted for guest:', toolId)
          return // Don't allow selection of restricted tools
        }
      }
      
      this.$emit('tool-changed', toolId)
    },
    
    updateToolOptions() {
      const currentOptions = this.getCurrentToolOptions()
      this.$emit('tool-options-changed', this.activeTool, currentOptions)
    },
    
    getCurrentToolOptions() {
      switch (this.activeTool) {
        case 'select':
        case 'undo':
        case 'redo':
          return {}
        case 'pen':
          return { ...this.penOptions }
        case 'rectangle':
          return { ...this.rectangleOptions }
        case 'circle':
          return { ...this.circleOptions }
        default:
          return {}
      }
    },
    
    clearCanvas() {
      // This would emit an event to clear the canvas
      this.$emit('clear-canvas')
    }
  },
  mounted() {
    // Initialize tool options
    this.updateToolOptions()
  }
}
</script>

<style scoped>
.toolbox {
  height: 100%;
  overflow-y: auto;
  padding: 0;
  font-size: 14px;
}

.toolbox-title {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-align: center;
  padding-bottom: 6px;
  border-bottom: 2px solid #e0e0e0;
}

.tool-section {
  margin-bottom: 16px;
}

.tool-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.tool-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 6px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 11px;
  min-height: 50px;
}

.tool-btn:hover {
  border-color: #007bff;
  background-color: #f8f9fa;
}

.tool-btn.active {
  border-color: #007bff;
  background-color: #007bff;
  color: white;
}

.tool-btn.select-tool {
  border-color: #28a745;
}

.tool-btn.select-tool:hover {
  border-color: #28a745;
  background-color: #f8f9fa;
}

.tool-btn.select-tool.active {
  border-color: #28a745;
  background-color: #28a745;
  color: white;
}

/* Undo/Redo tool styles */
.tool-btn.undo-tool,
.tool-btn.redo-tool {
  border-color: #17a2b8;
}

.tool-btn.undo-tool:hover,
.tool-btn.redo-tool:hover {
  border-color: #17a2b8;
  background-color: #f8f9fa;
  color: #17a2b8;
}

.tool-btn.undo-tool.active,
.tool-btn.redo-tool.active {
  border-color: #17a2b8;
  background-color: #17a2b8;
  color: white;
}

/* Restricted tool styles */
.tool-btn.restricted {
  border-color: #dc3545 !important;
  background-color: #fff5f5 !important;
  color: #dc3545 !important;
  cursor: not-allowed;
  opacity: 0.7;
  position: relative;
}

.tool-btn.restricted:hover {
  border-color: #dc3545 !important;
  background-color: #fff5f5 !important;
  color: #dc3545 !important;
}

.tool-btn.restricted.active {
  border-color: #dc3545 !important;
  background-color: #dc3545 !important;
  color: white !important;
}

.restriction-icon {
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 8px !important;
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.restricted-text {
  color: #dc3545 !important;
  font-weight: 500;
}

.tool-btn i {
  font-size: 16px;
  margin-bottom: 4px;
}

.tool-btn span {
  font-weight: 500;
  text-align: center;
}

/* Compact Quick Options */
.quick-options {
  margin-bottom: 16px;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.options-compact {
  width: 100%;
}

.option-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.color-input {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  background: none;
}

.size-slider {
  flex: 1;
  min-width: 60px;
  height: 6px;
  border-radius: 3px;
  background: #ddd;
  outline: none;
  cursor: pointer;
}

.size-display {
  font-size: 11px;
  font-weight: 500;
  color: #666;
  min-width: 35px;
  text-align: center;
}

.fill-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  user-select: none;
}

.fill-toggle input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}

/* Canvas Actions */
.canvas-actions {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.action-btn {
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #dc3545;
  border-radius: 6px;
  background-color: #ffffff;
  color: #dc3545;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.action-btn:hover {
  background-color: #dc3545;
  color: white;
}

.action-btn i {
  font-size: 14px;
}

/* Permission notices */
.permission-notice {
  margin-top: 8px;
  text-align: center;
}

.permission-notice small {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #6c757d;
  font-size: 10px;
  font-style: italic;
}

.permission-notice i {
  font-size: 9px;
}

/* Disabled state */
.action-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Custom scrollbar */
.toolbox::-webkit-scrollbar {
  width: 4px;
}

.toolbox::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.toolbox::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.toolbox::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Mobile Optimizations */
@media (max-width: 768px) {
  .toolbox {
    font-size: 13px;
  }
  
  .toolbox-title {
    font-size: 15px;
    margin-bottom: 10px;
  }
  
  .tool-buttons {
    gap: 4px;
  }
  
  .tool-btn {
    padding: 6px 4px;
    font-size: 10px;
    min-height: 45px;
  }
  
  .tool-btn i {
    font-size: 14px;
    margin-bottom: 3px;
  }
  
  .quick-options {
    padding: 6px;
    margin-bottom: 12px;
  }
  
  .option-row {
    gap: 6px;
  }
  
  .color-input {
    width: 28px;
    height: 28px;
  }
  
  .size-display {
    font-size: 10px;
    min-width: 30px;
  }
  
  .fill-toggle {
    font-size: 10px;
  }
  
  .action-btn {
    padding: 6px 10px;
    font-size: 11px;
  }
  
  .permission-notice small {
    font-size: 9px;
    gap: 3px;
  }
  
  .permission-notice i {
    font-size: 8px;
  }
}
</style>
